import { useMemo } from 'react';
import { TDependsOn, TField } from '../types/types.ts';

export const checkCondition = (
  condition: { field: string; value?: string; booleanValue?: boolean },
  conditions: Record<string, string | boolean>,
  formData: Record<string, string | boolean> | null,
  metadata?: Record<string, string | boolean> | null,
) => {
  const conditionValue = condition.value || condition.booleanValue;

  if (conditions[condition.field] !== undefined) {
    return conditions[condition.field] === conditionValue;
  }

  if (formData && formData[condition.field] !== undefined) {
    return typeof conditionValue === 'boolean'
      ? !!formData[condition.field] === conditionValue 
      : formData[condition.field] === conditionValue 
  }

  if (metadata && metadata[condition.field] !== undefined) {
    return typeof conditionValue === 'boolean'
      ? !!metadata[condition.field] === conditionValue 
      : metadata[condition.field] === conditionValue 
  }

  // If condition.value is true and the field is not found in conditions or formData, do not show the field
  return !conditionValue;
};

/**
 * Custom hook to conditionally render fields based on conditions and form data.
 *
 * This hook filters the provided fields based on the `dependsOn` method, which evaluates the
 * conditions and form data. If a field has a `dependsOn` method, it is used to determine whether
 * the field should be included in the rendered output. Additionally, fields with `storeCondition`
 * will be considered for setting store-based conditions.
 *
 * @param fields - Array of fields to be conditionally rendered.
 * @param conditions - An object representing the conditions for rendering fields.
 * @param formData - An object representing the current form data.
 *
 * @returns An array of fields that should be rendered.
 */
const useConditionalFields = <T extends TField & { dependsOn?: TDependsOn[] }>(
  fields: T[],
  conditions: Record<string, string | boolean>,
  formData: Record<string, string | boolean> | null,
  metadata?: Record<string, string | boolean> | null,
) => useMemo(() => fields.filter(field => {
    if (field.dependsOn?.length) {
      return field.dependsOn?.every(condition =>
        checkCondition(condition, conditions, formData, metadata),
      );
    }

    return true;
  }), [conditions, formData, fields]);

export default useConditionalFields;
